/**
 * Survey IDs are generated on the device, before any network is available, and
 * double as the idempotency key for submission. Format:
 *
 *   TS-YYYYMMDD-XXXXXX
 *
 * The suffix is 6 hex characters of cryptographically random data (~16.7M
 * values per day), not a sequence, so two offline devices cannot collide in
 * practice and no server round-trip is needed.
 */

const SUFFIX_LENGTH = 6;
const ALPHABET = '0123456789ABCDEF';

export const SURVEY_ID_PATTERN = /^TS-\d{8}-[0-9A-F]{6}$/;

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    cryptoObj.getRandomValues(bytes);
    return bytes;
  }
  // Very old browsers only. Still unique enough when combined with the date.
  for (let i = 0; i < length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return bytes;
}

/** "20260918" for the given local date. */
export function compactLocalDate(date: Date = new Date()): string {
  const y = date.getFullYear().toString().padStart(4, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}${m}${d}`;
}

export function generateSurveyId(date: Date = new Date()): string {
  const bytes = randomBytes(SUFFIX_LENGTH);
  let suffix = '';
  for (let i = 0; i < SUFFIX_LENGTH; i++) {
    suffix += ALPHABET[bytes[i] % ALPHABET.length];
  }
  return `TS-${compactLocalDate(date)}-${suffix}`;
}

export function isValidSurveyId(id: string): boolean {
  return SURVEY_ID_PATTERN.test(id);
}

/** Random id for sync-queue rows (not user facing). */
export function generateQueueId(): string {
  const bytes = randomBytes(8);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
